"use client";
import ResourceFilterAndBannerWrapper from "@/app/components/filters/ResourceFilterAndBannerWrapper";
import { QuestionDifficulty, QuestionFormat } from "@/enums/contentTypes";
import {
  Button,
  Card,
  Divider,
  Group,
  Stack,
  Stepper,
  useComputedColorScheme,
  useMantineTheme,
} from "@mantine/core";
import ChecklistRounded from "@mui/icons-material/ChecklistRounded";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import { useMediaQuery } from "@mui/material";
import { MainMetaMap, MainMetaMapSubTopic } from "@tsTypes/meta-schema";
import {
  QuestionTotalsBySubTopic,
  SiteQuestionTotal,
  SiteQuestionTotalsByFormat,
} from "@tsTypes/test-builder-schema";
import { useCheckUserAccess } from "@utils/accessChecks";
import { getTopicTitleWithHLIndicator, titleToUrl } from "@utils/routing";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import RestrictedResourceBanner from "../../../../components/banners/RestrictedResourceBanner";
import TakeExistingTestPrompt from "../../exam-tests/TakeExistingTestPrompt";
import StartTestButton from "./Stepper/StartTestButton";
import TestFormatStep from "./Stepper/TestFormatStep";
import TestPreviewStep from "./Stepper/TestPreviewStep";
import TestTypeAndContentStep from "./Stepper/TestTypeAndContentStep";
import {
  LimitType,
  TestBuilderSubjectOptions,
  TestBuilderSubTopicOptions,
  TestBuilderTopicOptions,
  TestFormats,
} from "./Stepper/types";

export default function BuilderLayout({
  subjectDocumentId,
  subjectNameLower,
  questionTotalsBySubTopic,
  mainMetaMap,
}: {
  subjectDocumentId: string;
  subjectNameLower: string;
  questionTotalsBySubTopic: QuestionTotalsBySubTopic;
  mainMetaMap: MainMetaMap;
}) {
  const router = useRouter();
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
  const userAccess = useCheckUserAccess("test builder", subjectDocumentId);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedSubTopicDocumentIds, setSelectedSubTopicDocumentIds] =
    useState<string[]>([]);
  const [limitType, setLimitType] = useState<LimitType>("time");
  const [selectedDifficulties, setSelectedDifficulties] = useState<
    QuestionDifficulty[]
  >([]);
  const [mcAbsoluteProportion, setMcAbsoluteProportion] = useState<number>(0);
  const [limitValue, setLimitValue] = useState<number>(0);
  // build main options
  const { subjectOptions, topicOptions, subTopicOptions } = useMemo(() => {
    const subjectOptions: TestBuilderSubjectOptions = {};
    const topicOptions: TestBuilderTopicOptions = {};

    Object.entries(mainMetaMap).forEach(([documentId, meta]) => {
      if (isSubjectMeta(meta)) {
        subjectOptions[documentId] = meta.title;
      } else if (isTopicMeta(meta)) {
        if (meta.subjectDocumentId === subjectDocumentId) {
          topicOptions[documentId] = {
            number: meta.number,
            title: getTopicTitleWithHLIndicator(meta),
            isHL: meta.isHL,
          };
        }
      } else {
        return;
      }
    });

    return {
      subjectOptions,
      topicOptions,
      subTopicOptions: getAllSubTopicIdsFromSubjectDocumentId(),
    };

    function getAllSubTopicIdsFromSubjectDocumentId() {
      const topicDocumentIds = Object.entries(mainMetaMap)
        .filter(([key, meta]) => {
          if (isTopicMeta(meta)) {
            return meta.subjectDocumentId === subjectDocumentId;
          }
        })
        .map(([key, meta]) => key);
      return Object.entries(mainMetaMap).reduce(
        (acc: TestBuilderSubTopicOptions, [documentId, meta]) => {
          if (
            isSubTopicMeta(meta) &&
            topicDocumentIds.includes(meta.topicDocumentId)
          ) {
            acc[documentId] = {
              label: meta.label,
              title: meta.title,
              isHL: meta.isHL,
              topicDocumentId: meta.topicDocumentId,
            };
          }
          return acc;
        },
        {}
      );
    }
  }, [mainMetaMap, subjectDocumentId]);

  const testBuilderQuestionLimits = useMemo(() => {
    return Object.entries(questionTotalsBySubTopic).reduce(
      (acc: SiteQuestionTotalsByFormat, [subTopicDocumentId, subTopicMeta]) => {
        if (!selectedSubTopicDocumentIds.includes(subTopicDocumentId)) {
          return acc;
        }
        (
          Object.entries(subTopicMeta) as [
            QuestionDifficulty,
            SiteQuestionTotalsByFormat
          ][]
        ).forEach(([difficulty, difficultyMeta]) => {
          if (!selectedDifficulties.includes(difficulty)) {
            return;
          }
          (
            Object.entries(difficultyMeta) as Array<
              [QuestionFormat, SiteQuestionTotal]
            >
          ).forEach(([questionType, questionMeta]) => {
            acc[
              questionType as keyof SiteQuestionTotalsByFormat
            ].numQuestions += questionMeta.numQuestions;
            acc[questionType as keyof SiteQuestionTotalsByFormat].numMarks +=
              questionMeta.numMarks;
          });
        });
        return acc;
      },
      {
        ...getBlankSiteQuestionTotalsByFormat(),
      }
    );
  }, [
    questionTotalsBySubTopic,
    selectedSubTopicDocumentIds,
    selectedDifficulties,
  ]);

  const toggleDifficulty = (difficulty: QuestionDifficulty) => {
    setSelectedDifficulties((prev) =>
      prev.includes(difficulty)
        ? prev.filter((level) => level !== difficulty)
        : [...prev, difficulty]
    );
  };

  const handleNext = () => {
    setActiveStep((current) => {
      const nextStep = Math.min(current + 1, 3);
      return nextStep;
    });
    router.push("#builderStepper");
  };

  const handleBack = () => {
    setActiveStep((current) => current - 1);
    router.push("#builderStepper");
  };

  const steps = [
    {
      label: "Subject & content",
      description: "What to include",
      icon: <MenuBookRounded fontSize="small" />,
    },
    {
      label: "Format",
      description: "Set time & mix",
      icon: <TuneRounded fontSize="small" />,
    },
    {
      label: "Review",
      description: "Let's go!",
      icon: <ChecklistRounded fontSize="small" />,
    },
  ] as const;

  return (
    <>
      <ResourceFilterAndBannerWrapper hasFilters={false}>
        <RestrictedResourceBanner
          resource={"test builder"}
          subjectDocumentId={subjectDocumentId}
          subjectName={subjectNameLower}
          userAccess={userAccess}
        />
      </ResourceFilterAndBannerWrapper>
      <Card
        withBorder
        radius="lg"
        p="xl"
        mb="xl"
        style={{
          padding: `calc(${theme.spacing.xl} * 1.5)`,
          background: isDark ? theme.colors.dark[7] : "rgba(255,255,255,0.9)",
          backdropFilter: "blur(8px)",
          overflow: "visible",
          zIndex: 1,
        }}
      >
        <Stepper
          // @ts-ignore
          id={"builderStepper"}
          color="darkblue"
          orientation={isMobile ? "vertical" : "horizontal"}
          active={activeStep}
          onStepClick={(value) => {
            setActiveStep(value);
            router.push("#builderStepper");
          }}
          iconSize={34}
          radius="md"
          mb={"4rem"}
          styles={{
            step: {
              transition: "transform 150ms ease, box-shadow 150ms ease",
              margin: isMobile ? undefined : "0 auto 1rem",
            },
            stepWrapper: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            },
            stepCompletedIcon: {
              color: theme.white,
            },
            stepLabel: {
              fontWeight: 650,
              fontSize: theme.fontSizes.sm,
              color: isDark ? theme.colors.gray[1] : theme.colors.dark[4],
            },
            stepDescription: {
              fontSize: theme.fontSizes.xs,
              marginTop: 2,
              color: isDark ? theme.colors.dark[0] : theme.colors.gray[6],
            },
          }}
        >
          <Stepper.Step
            label={steps[0].label}
            description={steps[0].description}
            icon={steps[0].icon}
          >
            <Stack gap="3rem">
              <Divider color="gray.1" mt="lg" />
              <TestTypeAndContentStep
                subjectOptions={subjectOptions}
                selectedSubjectId={subjectDocumentId}
                onSelectSubject={(subjectId) => {
                  if (subjectId !== subjectDocumentId) {
                    return router.push(
                      `/ib/${titleToUrl(
                        mainMetaMap[subjectId].title
                      )}/test-builder#builderStepper`
                    );
                  }
                  setSelectedSubTopicDocumentIds([]);
                }}
                topicOptions={topicOptions}
                subTopicOptions={subTopicOptions}
                selectedSubTopicDocumentIds={selectedSubTopicDocumentIds}
                onSetSelectedSubTopicDocumentIds={
                  setSelectedSubTopicDocumentIds
                }
                selectedDifficulties={selectedDifficulties}
                onToggleDifficulty={toggleDifficulty}
              />
            </Stack>
          </Stepper.Step>
          <Stepper.Step
            label={steps[1].label}
            description={steps[1].description}
            icon={steps[1].icon}
          >
            <Stack gap="3rem" mb="5vh">
              <Divider color="gray.1" mt="lg" />
              <TestFormatStep
                subjectNameLower={subjectNameLower}
                limitType={limitType}
                limitValue={limitValue}
                mcAbsoluteProportion={mcAbsoluteProportion}
                numSelectedSubTopics={selectedSubTopicDocumentIds.length}
                testBuilderQuestionLimits={testBuilderQuestionLimits}
                onLimitTypeChange={setLimitType}
                onLimitValueChange={setLimitValue}
                onRatioChange={setMcAbsoluteProportion}
              />
            </Stack>
          </Stepper.Step>
          <Stepper.Step
            label={steps[2].label}
            description={steps[2].description}
            icon={steps[2].icon}
          >
            <Stack gap="3rem" mb="10rem">
              <Divider color="gray.1" mt="lg" />
              <TestPreviewStep
                subjectNameLower={subjectNameLower}
                numSelectedTopics={
                  getAllTopicIdsFromSubTopicDocumentIds(
                    mainMetaMap,
                    selectedSubTopicDocumentIds
                  ).length
                }
                testType={TestFormats.custom}
                numSelectedSubTopics={selectedSubTopicDocumentIds.length}
                selectedDifficulties={selectedDifficulties}
                limitType={limitType}
                limitValue={limitValue}
                mcAbsoluteProportion={mcAbsoluteProportion}
              />
            </Stack>
          </Stepper.Step>
        </Stepper>
      </Card>
      <Group justify="space-between" mb="10rem" style={{ zIndex: 0 }}>
        <Button
          color="darkblue"
          variant="default"
          onClick={() => handleBack()}
          disabled={activeStep === 0}
        >
          Back
        </Button>
        {activeStep === 2 ? (
          <div
            style={{
              marginTop: `calc(${theme.spacing.xl} * 1.5)`,
              marginBottom: `calc(${theme.spacing.xl} * 1.5)`,
            }}
          >
            <StartTestButton
              userAccess={userAccess}
              subjectNameLower={subjectNameLower}
              subjectDocumentId={subjectDocumentId}
              subTopicDocumentIds={selectedSubTopicDocumentIds}
              testType={TestFormats.custom}
              selectedDifficulties={selectedDifficulties}
              limitType={limitType}
              limitValue={limitValue}
              mcAbsoluteProportion={mcAbsoluteProportion}
              size="sm"
            />
          </div>
        ) : (
          <Button
            color="darkblue"
            disabled={
              (activeStep === 0 &&
                (selectedSubTopicDocumentIds.length === 0 ||
                  selectedDifficulties.length === 0)) ||
              (activeStep === 1 && !limitValue)
            }
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Group>
      <TakeExistingTestPrompt subjectNameLower={subjectNameLower} />
    </>
  );
}

export function isSubjectMeta(meta: MainMetaMap[keyof MainMetaMap]) {
  return !("index" in meta);
}

function isTopicMeta(meta: MainMetaMap[keyof MainMetaMap]) {
  return "subjectDocumentId" in meta;
}

function isSubTopicMeta(meta: MainMetaMap[keyof MainMetaMap]) {
  return "topicDocumentId" in meta;
}

export function getAllTopicIdsFromSubTopicDocumentIds(
  mainMetaMap: MainMetaMap,
  subTopicDocumentIds: string[]
) {
  return subTopicDocumentIds
    .map((id) => {
      return (mainMetaMap[id] as MainMetaMapSubTopic).topicDocumentId;
    })
    .filter((value, index, self) => self.indexOf(value) === index);
}

function getBlankSiteQuestionTotalsByFormat(): SiteQuestionTotalsByFormat {
  return {
    MC: { numQuestions: 0, numMarks: 0 },
    LA: { numQuestions: 0, numMarks: 0 },
  };
}
